import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  Chip,
} from "@mui/material";

import { deleteJobOrder } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";

import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import WorkIcon from "@mui/icons-material/Work";
import BadgeIcon from "@mui/icons-material/Badge";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { 
  JOB_TYPES,
  JOB_DESCRIPTIONS,
  DOCUMENTS_TO_BRING,
  EQUIPMENT
} from "./utils/JobOrderConstants";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

// Helper function to format job type strings
const formatJobType = (typeStr) => {
  if (!typeStr) return '';
  return typeStr
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};


function DeleteJobOrder() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [client, setClient] = useState({})

  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({
    address: "",
    client: {
      name: "",
      address: {
        id: "",
        area: "",
      },
      contact: {
        id: "",
        email: {
          id: "",
        },
        number: {
          id: "",
        },
      },
    },
    job_types: [],
    job_description: [],
    competencies: [],
    specialization: {
      general_pest_control: [],
      termite: [],
    },
    job_date: "",
    start_time: "",
    end_time: "",
    technicians: [],
    endorser_name: "",
    endorser_contact_number: "",
    current_status: "",
    documents_to_bring: [],
    payment_method: "",
    schedule_confirmed: "",
    client_complaints: "",
    equipment: [],
    form_type: "",
    appointment_type: "",
    treatment_type: "",
  }); // State to store the job order
  const [loading, setLoading] = useState(true); // State to manage loading status
  const [error, setError] = useState(null); // State to manage error messages

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        const data = await getJobOrder(id); // Call the API
        const client = await getClient(data.client_id);  // Fetch client using client_id from job order
        setClient(client);
        setJobOrder(data); // Set the job order data
      } catch (err) {
        setError("Failed to fetch job order"); // Set error message
      } finally {
        setLoading(false); // Stop loading
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, [id]); // Dependency array ensures the effect runs when ID changes

  async function handleSubmit(e) {
    e.preventDefault();

    if (window.confirm("Are you sure you want to delete this Job Order?")) {
      setErrorMessage("");
      setIsLoading(true);
      try {
        // TODO: remove logs after testing
        console.log("Job Order Id from useParams:", id);
        console.log("Job Order being sent to the backend:", jobOrder);

        await deleteJobOrder(id, jobOrder);

        setSuccessMessage("Job Order deleted successfully!");
      } catch (error) {
        setErrorMessage(
          `An error occurred while deleting the Job Order: ${error.message}. Please check your network connection and try again.`
        );
      } finally {
        setIsLoading(false);
        setTimeout(() => {
          navigate("/manage-job-orders");
        }, 3000);
      }
    }
  }


  return (
    <BaseFormWrapper
      titleIcon={<WorkIcon />}
      title="Operations"
      subTitle="Manage Job Orders"
      subTitleRoute="/manage-job-orders"
      formTitle="Delete Job Order"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit} className="job-order-form">
        <Grid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Client Information
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Job Order ID row */}
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Job Order ID"
                      value={id || ""}
                      size="small"
                      fullWidth
                      disabled
                    />
                  </Grid>

                  {/* Client Name + Client ID + Work Permit chip */}
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Client Name"
                          variant="outlined"
                          fullWidth
                          value={jobOrder?.client?.name || ""}
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Client ID"
                          variant="outlined"
                          fullWidth
                          value={jobOrder?.client?.id || ""}
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md="auto" sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                        {client?.work_permit_required ? (
                          <Chip
                            icon={<BadgeIcon sx={{ fontSize: 18 }} />}
                            label="WORK PERMIT REQUIRED"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "rgba(25, 118, 210, 0.6)",
                              color: "#1976d2",
                              bgcolor: "rgba(25, 118, 210, 0.08)",
                              fontWeight: 600,
                              letterSpacing: "0.5px",
                              height: 30,
                            }}
                          />
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={10}>
                        <TextField
                          fullWidth
                          label="Job Site Address"
                          value={
                              [
                                jobOrder.client.address?.house_unit_street || "",
                                jobOrder.client.address?.barangay           || "",
                                jobOrder.client.address?.city               || "",
                                jobOrder.client.address?.metro_province     || ""
                              ]
                                .filter(part => part)             
                                .join(", ")                        
                              + (jobOrder.client.address?.zipcode
                                  ? ` ${jobOrder.client.address.zipcode}`
                                  : ""
                                )
                            }
                          InputProps={{
                            disabled: true,
                          }}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder.client.address?.area || ""}
                          fullWidth
                          disabled
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={`${jobOrder.client.contact?.firstname} ${jobOrder.client.contact?.lastname}`.trim()}
                      InputProps={{
                        disabled: true,
                      }}
                    />

                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      value={jobOrder.client.contact?.email?.email || ""}
                      InputProps={{
                        disabled: true,
                      }}
                    />

                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      value={`${jobOrder.client.contact?.number?.type || ''}: ${jobOrder.client.contact?.number?.number || ''}`.trim()}
                      InputProps={{
                        disabled: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Category"
                      variant="outlined"
                      value={client?.category || ''}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Parent Client"
                      variant="outlined"
                      value={client?.parent_client?.name || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Job Type
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...JOB_TYPES]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option
                      .toLowerCase()
                      .replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={(jobOrder.job_types || []).includes(formattedOption)}
                            disabled
                          />
                        }
                        label={option}
                      />

                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              style={{ overflowX: 'auto' }}
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Job Description
                  </Typography>
                }
                sx={{
                  padding: 0
                }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...JOB_DESCRIPTIONS]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option
                      .toLowerCase()
                      .replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={(jobOrder.job_description || []).includes(formattedOption)}
                            disabled
                          />
                        }
                        label={option}
                      />

                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography

                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Schedule Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Schedule
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={2}>
                    <TextField
                      label="Date"
                      name="job_date"
                      type="date"
                      value={jobOrder.job_date || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Start Time"
                      name="start_time"
                      type="time"
                      value={jobOrder.start_time || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="End Time"
                      name="end_time"
                      type="time"
                      value={jobOrder.end_time}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="h6">Appointment</Typography>}
                sx={{ padding: 0 }}
              />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Appointment Type */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appointment-type-label">
                        Appointment Type
                      </InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        value={jobOrder?.appointment_type || ""}
                        label="Appointment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Treatment Type */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="treatment-type-label">
                        Treatment Type
                      </InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        value={jobOrder?.treatment_type || ""}
                        label="Treatment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Endorsement Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Typography>
                    Endorsement Form
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <TextField
                      label="Endorser Name"
                      name="endorser_name"
                      type="text"
                      value={jobOrder.endorser_name || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Endorser Contact Number"
                      name="endorser_contact_number"
                      type="text"
                      value={jobOrder.endorser_contact_number || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Current Status"
                      name="current_status"
                      value={jobOrder.current_status || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    >
                      <MenuItem value="heavily_infested">Heavily Infested</MenuItem>
                      <MenuItem value="medium_infestation">Medium Infestation</MenuItem>
                      <MenuItem value="light_infestation">Light Infestation</MenuItem>
                      <MenuItem value="for_inspection_first_before_treament">
                        For Inspection First Before Treament
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Payment Method"
                      name="payment_method"
                      value={jobOrder.payment_method || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    >
                      <MenuItem value="COD">COD</MenuItem>
                      <MenuItem value="For Billing">For Billing</MenuItem>
                      <MenuItem value="Check collection">Check collection</MenuItem>
                      <MenuItem value="Bank deposit">Bank deposit</MenuItem>
                      <MenuItem value="Online/Gcash">Online/Gcash</MenuItem>
                      <MenuItem value="Paymongo">Paymongo</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Schedule Confirmed with Clients"
                      name="schedule_confirmed"
                      value={jobOrder.schedule_confirmed || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="With Client Complaints"
                      name="client_complaints"
                      value={jobOrder.client_complaints || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Required Documents Section */}
          <Grid item xs={12}>
            <Card variant="outlined" style={{ overflowX: "auto" }}>
              <CardHeader
                title={
                  <Typography variant="h6">
                    Required Documents to Bring
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...DOCUMENTS_TO_BRING]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={(jobOrder.documents_to_bring || []).includes(formattedOption)}
                            disabled
                          />
                        }
                        label={option}
                      />
                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          {/* Equipment Section */}
          <Grid item xs={12}>
            <Card variant="outlined" style={{ overflowX: "auto" }}>
              <CardHeader
                title={
                  <Typography variant="h6">
                    Equipment to Bring
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent
                sx={{
                  padding: "0 !important",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, auto)",
                  gap: 1,
                }}
              >
                {[...EQUIPMENT]
                  .sort((a, b) => a.localeCompare(b))
                  .map((option) => {
                    const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                    return (
                      <FormControlLabel
                        key={formattedOption}
                        control={
                          <Checkbox
                            name={formattedOption}
                            checked={(jobOrder.equipment || []).includes(formattedOption)}
                            disabled
                          />
                        }
                        label={option}
                      />
                    );
                  })
                }
              </CardContent>
            </Card>
          </Grid>

          {/* Technicians Section */}
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Technicians
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {jobOrder.technicians.length > 0 ? (
                        jobOrder.technicians.map(
                          (technician, index) => (
                            <Grid item xs={12} key={technician.id}>
                              <TextField
                                label={`Technician ${index + 1}`}
                                variant="outlined"
                                fullWidth
                                value={`${technician.firstname} ${technician.lastname}`}
                                disabled
                              />
                            </Grid>
                          )
                        )
                      ) : (
                        <Grid item xs={12}>
                          <Typography
                            variant="body1"
                            color="textSecondary"
                          >
                            No technicians selected.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Form Type Section */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="h6">Form Type</Typography>}
                sx={{ padding: 0 }}
              />
              <CardContent>
                <TextField
                  select
                  label="Form Type"
                  value={jobOrder.form_type || ""}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="makati_gpc_form">Makati GPC Form</MenuItem>
                  <MenuItem value="qc_form">QC Form</MenuItem>
                  <MenuItem value="termite_control_form">Termite Control Form</MenuItem>
                  <MenuItem value="transmittal_form">Transmittal Form</MenuItem>
                </TextField>
              </CardContent>
            </Card>
          </Grid>

          {/* Cancel and Delete Buttons Section */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/manage-job-orders")}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Delete
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default DeleteJobOrder;
